import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { useAuth } from '../api/AuthContext.js';
import { fetchAllFutureEventsByOwner } from '../api/fetchAllEvents.js';
import { fetchAllParticipantsByOwner } from '../api/fetchParticipants.js';
import { getSubscriptionStatus } from '../api/subscriptions.js';
import EventItemAdmin from '../components/EventItemAdmin.js';
import ParticipantOverview from '../components/ParticipantOverview.js';
import SubscriptionBanner from '../components/SubscriptionBanner.js';

function Dashboard() {
    const auth = useAuth();
    const [events, setEvents] = useState(null);
    const [participants, setParticipants] = useState(null);
    const [subscriptionData, setSubscriptionData] = useState(null);

    // Get all events
    const getAllEvents = async () => {
        const eventData = await fetchAllFutureEventsByOwner(
            auth.currentUser.user_id
        );
        setEvents(eventData);
    };

    useEffect(() => {
        getAllEvents();
    }, []);

    const getParticipants = async () => {
        const participantsData = await fetchAllParticipantsByOwner(
            auth.currentUser.user_id
        );
        setParticipants(participantsData);
    };

    useEffect(() => {
        getParticipants();
    }, []);

    // Get subscription status
    const fetchSubscription = async () => {
        try {
            const res = await getSubscriptionStatus();
            setSubscriptionData(res);
        } catch (err) {
            console.error('Error fetching subscription:', err);
        }
    };

    useEffect(() => {
        fetchSubscription();
    }, []);

    const navigate = useNavigate();

    const handleAddEvent = () => {
        navigate('/admin/events/create');
    };

    const handleAddParticipant = () => {
        navigate('/admin/participants/');
    };

    // Determine states
    const hasEvents = events && events.length > 0;
    const hasParticipants = participants && participants.length > 0;
    const isLoadingEvents = events === null;
    const isLoadingParticipants = participants === null;

    return (
        <>
            <div>
                <h1 id="pageDashboard">Dashboard</h1>

                {/* Subscription status banner - show at top of dashboard */}
                {subscriptionData && (
                    <SubscriptionBanner
                        subscriptionData={subscriptionData}
                        context="general"
                    />
                )}

                <hr />

                <h2>Events</h2>

                {/* Empty state for events */}
                {!isLoadingEvents && !hasEvents && subscriptionData && (
                    <div style={{
                        textAlign: 'center',
                        padding: '40px 20px',
                        background: '#f9fafb',
                        borderRadius: '8px',
                        marginBottom: '20px'
                    }}>
                        <div style={{ fontSize: '36px', marginBottom: '12px' }}>📅</div>
                        <h3 style={{ marginBottom: '8px', color: '#1f2937' }}>
                            No events yet
                        </h3>
                        <p style={{
                            fontSize: '14px',
                            color: '#6b7280',
                            marginBottom: '16px'
                        }}>
                            Create your first event to get started - it's free!
                        </p>
                        <button
                            id="buttonCreateNewEvent"
                            className="button is-primary is-small"
                            onClick={handleAddEvent}
                        >
                            + Create your first event
                        </button>
                    </div>
                )}

                {/* Loading state */}
                {isLoadingEvents && <div>Loading events...</div>}

                {/* Events table */}
                {!isLoadingEvents && hasEvents && (
                    <>
                        <div style={{
                            backgroundColor: 'white',
                            border: '1px solid #e5e7eb',
                            borderRadius: '8px',
                            overflow: 'hidden',
                            marginBottom: '16px'
                        }}>
                            <table style={{
                                width: '100%',
                                borderCollapse: 'collapse'
                            }}>
                                <thead>
                                    <tr style={{
                                        backgroundColor: '#f9fafb',
                                        borderBottom: '2px solid #e5e7eb'
                                    }}>
                                        <th style={{
                                            padding: '8px 12px',
                                            textAlign: 'left',
                                            fontSize: '10px',
                                            fontWeight: '600',
                                            color: '#6b7280',
                                            textTransform: 'uppercase',
                                            letterSpacing: '0.05em',
                                            minWidth: '200px'
                                        }}>Event</th>
                                        <th style={{
                                            padding: '8px 12px',
                                            textAlign: 'center',
                                            fontSize: '10px',
                                            fontWeight: '600',
                                            color: '#6b7280',
                                            textTransform: 'uppercase',
                                            letterSpacing: '0.05em',
                                            width: '80px'
                                        }}>Status</th>
                                        <th style={{
                                            padding: '8px 12px',
                                            textAlign: 'left',
                                            fontSize: '10px',
                                            fontWeight: '600',
                                            color: '#6b7280',
                                            textTransform: 'uppercase',
                                            letterSpacing: '0.05em',
                                            minWidth: '180px'
                                        }}>Next Session</th>
                                        <th style={{
                                            padding: '8px 12px',
                                            textAlign: 'left',
                                            fontSize: '10px',
                                            fontWeight: '600',
                                            color: '#6b7280',
                                            textTransform: 'uppercase',
                                            letterSpacing: '0.05em'
                                        }}>Actions</th>
                                        <th style={{
                                            padding: '8px 12px',
                                            textAlign: 'center',
                                            fontSize: '10px',
                                            fontWeight: '600',
                                            color: '#6b7280',
                                            textTransform: 'uppercase',
                                            letterSpacing: '0.05em',
                                            width: '80px'
                                        }}>View</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {events.map((event) => {
                                        return (
                                            <EventItemAdmin
                                                key={event.event_id}
                                                event={event}
                                                refreshEvents={getAllEvents}
                                            />
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                        <button
                            id="buttonCreateNewEvent"
                            className="button is-primary is-small"
                            onClick={handleAddEvent}
                        >
                            + Create new
                        </button>
                    </>
                )}
                <br />
                <br />
                <h2>Participants</h2>

                {/* Empty state for participants */}
                {!isLoadingParticipants && !hasParticipants && (
                    <div style={{
                        textAlign: 'center',
                        padding: '40px 20px',
                        background: '#f9fafb',
                        borderRadius: '8px',
                        marginBottom: '20px'
                    }}>
                        <div style={{ fontSize: '36px', marginBottom: '12px' }}>👥</div>
                        <h3 style={{ marginBottom: '8px', color: '#1f2937' }}>
                            No participants yet
                        </h3>
                        <p style={{
                            fontSize: '14px',
                            color: '#6b7280',
                            marginBottom: '16px'
                        }}>
                            Add participants to start managing your events
                        </p>
                        <button
                            className="button is-primary is-small"
                            onClick={handleAddParticipant}
                        >
                            + Add your first participant
                        </button>
                    </div>
                )}

                {/* Loading state */}
                {isLoadingParticipants && <div>Loading participants...</div>}

                {/* Participants list */}
                {!isLoadingParticipants && hasParticipants && (
                    <>
                        <div className="block limit-height list">
                            <table style={{
                                width: '100%',
                                borderCollapse: 'collapse',
                                backgroundColor: '#fff',
                                borderRadius: '8px',
                                overflow: 'hidden',
                                boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)'
                            }}>
                                <thead>
                                    <tr style={{
                                        backgroundColor: '#f9fafb',
                                        borderBottom: '2px solid #e5e7eb'
                                    }}>
                                        <th style={{
                                            padding: '3px 8px',
                                            textAlign: 'left',
                                            fontSize: '10px',
                                            fontWeight: '600',
                                            color: '#6b7280',
                                            textTransform: 'uppercase',
                                            letterSpacing: '0.05em'
                                        }}></th>
                                        <th style={{
                                            padding: '3px 8px',
                                            textAlign: 'left',
                                            fontSize: '10px',
                                            fontWeight: '600',
                                            color: '#6b7280',
                                            textTransform: 'uppercase',
                                            letterSpacing: '0.05em'
                                        }}>Name</th>
                                        <th style={{
                                            padding: '3px 8px',
                                            textAlign: 'center',
                                            fontSize: '10px',
                                            fontWeight: '600',
                                            color: '#6b7280',
                                            textTransform: 'uppercase',
                                            letterSpacing: '0.05em'
                                        }}>Status</th>
                                        <th style={{
                                            padding: '3px 8px',
                                            textAlign: 'right',
                                            fontSize: '10px',
                                            fontWeight: '600',
                                            color: '#6b7280',
                                            textTransform: 'uppercase',
                                            letterSpacing: '0.05em'
                                        }}>Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {participants.map((participant) => (
                                        <ParticipantOverview
                                            key={participant.participant_id}
                                            participant={participant}
                                            refreshParticipants={getParticipants}
                                        />
                                    ))}
                                </tbody>
                            </table>
                        </div>
                        <button
                            className="button is-primary is-small"
                            onClick={handleAddParticipant}
                        >
                            + Add participant
                        </button>
                    </>
                )}
            </div>
        </>
    );
}

export default Dashboard;
